/*jshint esversion: 6 */
import React from 'react';
import { connect } from 'react-redux';
import { AppActions, UtilityActions } from 'app/redux/actions.js';
import Analytics from 'app/utility/Analytics.js';
import posthog from 'posthog-js';
import { RecordEventContext } from '@/lib/use-record-event.ts';

function mapStateToProps(state) {
    return({
        app: state.app,
        // Not inherited from UserProvider: compose() puts AppProvider outside
        // it, so the pub_id recordEvent fills in has to come from the store here.
        user: state.user
    });
}

function mapDispatchToProps(dispatch) {
    return {
        filterStore: (store, key, value, exact) => {
            dispatch(UtilityActions.filterStore(store, key, value, exact));
        },
        sortStore: (store, key, direction, groupFxns) => {
            dispatch(UtilityActions.sortStore(store, key, direction, groupFxns));
        },
        setApiError: (error) => {
            dispatch(AppActions.apiError(error));
        },
        deleteApiError: (keys) => {
            dispatch(AppActions.deleteApiError(keys));
        },
        setAppError: (error) => {
            dispatch(AppActions.setAppError(error));
        },
        deleteAppError: (keys) => {
            dispatch(AppActions.deleteAppError(keys));
        },
        loginUser: (user) => {
            dispatch(AppActions.loginUser(user));
        },
        createUser: (user) => {
            dispatch(AppActions.createUser(user));
        },
        autologin: (user) => {
            dispatch(AppActions.autologin(user));
        },
        verifyCode: (user) => {
            dispatch(AppActions.verifyCode(user));
        },
        resendCode: (user) => {
            dispatch(AppActions.resendCode(user));
        },
        confirmSignup: (data) => {
            dispatch(AppActions.confirmSignup(data));
        },
        checkUserState: (user) => {
            dispatch(AppActions.checkUserState(user));
        },
        addMessage: (message) => {
            dispatch(AppActions.addMessage(message));
        },
        deleteMessage: (types) => {
            dispatch(AppActions.deleteMessage(types));
        },
        deleteMessageById: (id) => {
            dispatch(AppActions.deleteMessageById(id));
        },
        setCanMoveError: () => {
            dispatch(AppActions.setCanMoveError());
        }
    };
}

function searchState(state, keys, field) {
    let matchedState = [];

    if (keys && !Array.isArray(keys)) {
        keys = [keys];
    } else if (keys === null || keys === undefined) {
        keys = [];
    }

    for (let i = 0; i < state.length; ++i) {
        if (field) {
            if (state[i].error.hasOwnProperty(field)) {
                if (state[i].error) {
                    if (state[i].post !== undefined) {
                        matchedState.push(Object.assign({}, state[i].error, {post: state[i].post}));
                    } else {
                        matchedState.push(state[i].error);
                    }
                    
                } else {
                    matchedState.push(state[i]);
                }

            }
        } else {
            for (let j = 0; j < keys.length; ++j) {
                if (state[i].type === keys[j]) {
                    if (state[i].error) {
                        if (state[i].post !== undefined) {
                            matchedState.push(Object.assign({}, state[i].error, {post: state[i].post}));
                        } else {
                            matchedState.push(state[i].error);
                        }
                    } else {
                        matchedState.push(state[i]);
                    }
                }
            }
        }
    }

    return matchedState;
}

export default function AppProvider(Component) {

    class AppProvider extends React.Component {
        constructor(props) {
            super(props);

            this.getApiErrors = this.getApiErrors.bind(this);
            this.getAppErrors = this.getAppErrors.bind(this);
            this.getPermErrors = this.getPermErrors.bind(this);
            this.getAppMessages = this.getAppMessages.bind(this);
            this.recordEvent = this.recordEvent.bind(this);
            this.cleanUrl = this.cleanUrl.bind(this);

            this.analytics = new Analytics(props.app.config.apiUrl);

        }

        getAppErrors(keys) {
            return searchState(this.props.app.errors.local, keys, null);
        }

        getApiErrors(keys) {
            return searchState(this.props.app.errors.api, keys, null);
        }

        getPermErrors(errors) {
            return searchState(this.props.app.errors.api, null, 'perms');
        }

        getAppMessages(keys) {
            return searchState(this.props.app.messages, keys, null);
        }

        recordEvent(data) {
            /* The class components all pass pub_id explicitly. Function
               components get it filled in here instead, so useRecordEvent can
               stay a plain useContext rather than reaching into the store. */
            const event = (data.pub_id === undefined)
                ? { ...data, pub_id: this.props.user.detail.id }
                : data;

            try {
                this.analytics.recordEvent(event);
            } catch (e) {
                // ok to fail silently here
            }

            // every in-app analytics event is mirrored to PostHog; pub_id is
            // dropped because PostHog attributes events to the identified user
            posthog.capture(data.event_name, data.event_data);
        }

        cleanUrl(url) {
            let cleanUrl = url;
            if (url.match(/#$/)) {
                cleanUrl = url.substring(0, url.length - 1);
            }
            return cleanUrl;
        }

        render() {
            return (
                <RecordEventContext.Provider value={this.recordEvent}>
                    <Component
                        getApiErrors={this.getApiErrors}
                        getAppErrors={this.getAppErrors}
                        getPermErrors={this.getPermErrors}
                        getAppMessages={this.getAppMessages}
                        recordEvent={this.recordEvent}
                        cleanUrl={this.cleanUrl}
                        {...this.props}/>
                </RecordEventContext.Provider>
            );
        }
    }

    AppProvider.displayName = "AppProvider";

    return connect(mapStateToProps, mapDispatchToProps)(AppProvider);
};
